import { useMemo } from 'react';
import useQuote from '~/hooks/use-quote';
import { BasketQuote, QuoteDates } from '~/lib/types/basket-types';
import useBasketItemQuote from './use-basket-item-quote';
import useBasket from './use-basket';
import { BasketSections } from '~/lib/types/enums';
import { isQuoteDropOff } from '~/lib/quotes';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getBasketCollectionDate } from '~/lib/basket';
import { TranslationNamespaces } from '~/lib/constants';

export const calculateDeliveryGuaranteeDate = (_: boolean, date: Date) => {
  return date;
  // const output = new Date(date);
  // if (isQuoteDropOff) {
  //   // Add a day to the max delivery date if it's after 12pm
  //   if (new Date().getHours() >= 12) {
  //     output.setDate(output.getDate() + 1);
  //   }

  //   // Add another day if its a drop off
  //   output.setDate(output.getDate() + 1);

  //   //If its a sunday, add another day
  //   if (output.getDay() === 0 || output.getDay() === 6) {
  //     output.setDate(output.getDate() + 1);
  //   }
  // }
  // return output;
};

export const useBasketItemDeliveryGuaranteeDate = (basketItemId: string) => {
  const { quote } = useBasketItemQuote(basketItemId);
  const { items } = useBasket();

  const item = useMemo(() => {
    return items[basketItemId];
  }, [items, basketItemId]);

  const dates = useMemo(() => {
    return item?.sections[BasketSections.DATES]?.payload as QuoteDates;
  }, [item]);

  const { deliveryGuaranteeDate } = useDeliveryGuaranteeDate(quote, dates);
  return deliveryGuaranteeDate;
};

export const useBasketDeliveryGuaranteeDate = () => {
  const minMaxDays = 1;
  const { quote } = useQuote();
  const { item } = useBasket();
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);

  const dates = useMemo(() => {
    return item?.sections[BasketSections.DATES]?.payload as QuoteDates;
  }, [item]);

  const { deliveryGuaranteeDate } = useDeliveryGuaranteeDate(quote, dates);

  const cutOffDate: { date: string | null; time: string | null } | null = useMemo(() => {
    const d = getBasketCollectionDate(dates);

    if (!d) {
      return null;
    }

    const match = quote?.dates?.find((x) => x.type == 'COLLECTION' && x.collection?.date === d && x.cutoff);

    if (!match) {
      return null;
    }

    const collectionToday = dayjs(match.cutoff).isSame(dayjs(), 'day');
    if (collectionToday) {
      return {
        date: t('TODAY', {
          defaultValue: 'today'
        }),
        time: dayjs(match.cutoff).format('LT')
      };
    }

    const collectionTomorrow = dayjs(match.cutoff).isSame(dayjs().add(1, 'day'), 'day');
    if (collectionTomorrow) {
      return {
        date: t('TOMORROW', {
          defaultValue: 'tomorrow'
        }),
        time: dayjs(match.cutoff).format('LT')
      };
    }

    return { date: dayjs(match.cutoff).format('ddd, Do MMMM'), time: dayjs(match.cutoff).format('LT') };
  }, [dates, quote, t]);

  const maxDays = useMemo(() => {
    if (quote?.metadata?.estimatedDeliveryDaysMax) {
      return Math.max(minMaxDays, quote.metadata.estimatedDeliveryDaysMax);
    }
    if (!deliveryGuaranteeDate) {
      return minMaxDays;
    }
    const today = new Date();
    const deliveryDate = new Date(deliveryGuaranteeDate);
    const timeDiff = deliveryDate.getTime() - today.getTime();
    return Math.max(minMaxDays, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  }, [deliveryGuaranteeDate, quote]);

  return {
    deliveryGuaranteeDate,
    maxDays: maxDays.toString(),
    cutOffTime: cutOffDate?.time,
    cutOffDate: cutOffDate?.date
  };
};

const useDeliveryGuaranteeDate = (quote: BasketQuote, dates: QuoteDates) => {
  const deliveryGuaranteeDate = useMemo(() => {
    if (!quote) {
      return null;
    }

    if (dates.dates && Array.isArray(dates.dates)) {
      const selected = dates.dates.find((x) => x.type == 'COLLECTION');
      if (selected) {
        const selectedDate = new Date(selected.date);
        const selectedQuoteDate = quote.dates.find(
          (x) =>
            x.type == 'COLLECTION' && x.collection && new Date(x.collection.date).getTime() === selectedDate.getTime()
        );
        const max = selectedQuoteDate?.delivery?.dateMax;
        if (max) {
          return calculateDeliveryGuaranteeDate(isQuoteDropOff(quote), new Date(max));
        }
      }
    }

    const maxDeliveryDate = quote?.dates.find((x) => x.type == 'COLLECTION')?.delivery?.dateMax;
    return maxDeliveryDate ? calculateDeliveryGuaranteeDate(isQuoteDropOff(quote), new Date(maxDeliveryDate)) : null;
  }, [dates, quote]);

  return { deliveryGuaranteeDate };
};
